/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';

export type InformationCardProps = {
    /**
     * The card title.
     */
    title: string;

    /**
     * The name of the card icon.
     */
    iconName?: string;

    /**
     * The color of the card icon.
     */
    iconColor?: string;

    /**
     * The card content.
     */
    children?: React.ReactNode;

    /**
     * Class name to add extra CSS properties.
     */
    className?: string;

    /**
     * Class name to add extra CSS properties to the caed title.
     */
    titleClassName?: string;
};

const { styleComponent } = getStylist('InformationCard');

const Title = styleComponent<ITextProps>(Text)('Title', theme => ({
    fontSize: theme.fontSize,
    lineHeight: theme.largeLineHeight,
    letterSpacing: theme.titleLetterSpacing,
    fontWeight: theme.titleFontWeight as Property.FontWeight
}));

const Card = styleComponent<IStackProps>(Stack)('Card', theme => ({
    color: theme.textColor,
    fontSize: theme.fontSize,
    maxWidth: theme.cardMaxWidth,
    margin: theme.cardOuterSpacing,
    padding: theme.cardInnerSpacing,
    borderRadius: theme.borderRadius,
    lineHeight: theme.mediumLineHeight,
    backgroundColor: theme.backgroundColor,
    letterSpacing: theme.contentLetterSpacing
}));

const ColoredIcon = styleComponent<IIconProps>(Icon)<{ iconColor: string }>('ColoredIcon', {}, ({ customProps: { iconColor } }) => ({
    color: iconColor
}));

/**
 * The Information card to display a piece of information with som description.
 *
 * @param props the properties of the Information card.
 */
export const InformationCard = (props: InformationCardProps) => {
    const { title, children, iconName, iconColor, className, titleClassName } = props;

    return (
        <Card className={className}>
            <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                <Title className={titleClassName} data-automation-id="title" variant="medium">
                    {title}
                </Title>
                <ColoredIcon hidden={!iconName} iconName={iconName} customProps={{ iconColor }} />
            </Stack>
            <Stack tokens={{ childrenGap: 12 }}>{children}</Stack>
        </Card>
    );
};
